import type { RequestFn } from '../http';
import type {
  Warehouse,
  WarehouseBin,
  Article,
  WarehousesResponseNative,
  BinsResponseNative,
  ArticlesResponse,
  OnHandResponseNative,
  MovementsResponse,
  CreateWarehouseInput,
  CreateBinInput,
  CreateArticleInput,
  StockSearchResponse,
  LowStockResponse,
  StockValuation,
  ValuationRefreshResult,
  StockReturn,
  StockReturnsResponse,
  CreateReturnInput,
  ReviewReturnInput,
  CanDeactivateResponse,
  DeliveryOrder,
  DeliveryOrdersResponse,
  CreateDeliveryOrderInput,
  PickDeliveryOrderInput,
  IssueDeliveryOrderInput,
  DeliveryOrderStatus,
  MaterialHistoryResponse,
  ReservationInput,
  ReleaseReservationInput,
  ReservationResult,
  SerialHistoryResult,
  ArticlePurchaseHistoryResponse,
} from '../types/lager';

interface ListOpts {
  q?: string;
  page?: number;
  pageSize?: number;
  includeInactive?: boolean;
}

function buildQuery(opts?: object): string {
  if (!opts) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Native Lager-API (ServiceHub-eigenes Inventar, /api/lager/*).
 * UUID-basiert, kein ConnectWise.
 */
/** Zeile in der Admin-Prüfliste für Technikeranlagen. */
export interface ArticleReviewRow {
  id: string;
  articleNumber: string;
  name: string;
  manufacturer: string | null;
  manufacturerPartNumber: string | null;
  barcode: string | null;
  unit: string;
  reviewStatus: 'PENDING' | 'APPROVED' | 'MERGED';
  reviewNote: string | null;
  createdAt: string;
  createdByName: string | null;
  /** Wie viele Bestände/Zählungen bereits am Artikel hängen — Kontext fürs Zusammenführen. */
  stockItemCount: number;
  inventoryLineCount: number;
  mergedIntoArticleId: string | null;
}

export function createLagerApi(request: RequestFn) {
  return {
    // --- Warehouses ---
    async lagerListWarehouses(opts?: ListOpts) {
      return request<WarehousesResponseNative>(`/lager/warehouses${buildQuery(opts)}`);
    },
    async lagerGetWarehouse(id: string) {
      return request<{ warehouse: Warehouse }>(`/lager/warehouses/${id}`);
    },
    async lagerCreateWarehouse(body: CreateWarehouseInput) {
      return request<{ warehouse: Warehouse }>(`/lager/warehouses`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerUpdateWarehouse(id: string, body: Partial<CreateWarehouseInput> & { isActive?: boolean }) {
      return request<{ warehouse: Warehouse }>(`/lager/warehouses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    async lagerCanDeactivateWarehouse(id: string) {
      return request<CanDeactivateResponse>(`/lager/warehouses/${id}/can-deactivate`);
    },
    async lagerCanDeactivateBin(id: string) {
      return request<CanDeactivateResponse>(`/lager/bins/${id}/can-deactivate`);
    },
    /** Admin: legt für alle Benutzer ohne persönliches Lager eines an (idempotent). */
    async lagerBackfillPersonalWarehouses() {
      return request<{ total: number; created: number; skipped: number }>(
        `/lager/backfill-personal`,
        { method: 'POST' }
      );
    },

    // --- Bins ---
    async lagerListBins(warehouseId: string, opts?: ListOpts) {
      return request<BinsResponseNative>(`/lager/warehouses/${warehouseId}/bins${buildQuery(opts)}`);
    },
    async lagerGetBin(id: string) {
      return request<{ bin: WarehouseBin & { warehouse: { id: string; name: string } } }>(`/lager/bins/${id}`);
    },
    async lagerCreateBin(warehouseId: string, body: CreateBinInput) {
      return request<{ bin: WarehouseBin }>(`/lager/warehouses/${warehouseId}/bins`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerUpdateBin(id: string, body: Partial<CreateBinInput> & { isActive?: boolean }) {
      return request<{ bin: WarehouseBin }>(`/lager/bins/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    /** Drag-&-Drop-Sortierung: setzt die Rangfolge der Lagerplätze neu. */
    async lagerReorderBins(warehouseId: string, orderedIds: string[]) {
      return request<{ updated: number }>(`/lager/warehouses/${warehouseId}/bins/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ orderedIds }),
      });
    },
    async lagerGetBinOnHand(binId: string, opts?: ListOpts) {
      return request<OnHandResponseNative>(`/lager/bins/${binId}/onhand${buildQuery(opts)}`);
    },

    // --- Articles ---
    async lagerListArticles(opts?: ListOpts & { category?: string }) {
      return request<ArticlesResponse>(`/lager/articles${buildQuery(opts)}`);
    },
    async lagerSearchArticles(q: string) {
      return request<{ articles: Article[] }>(`/lager/articles/search${buildQuery({ q })}`);
    },
    async lagerGetArticle(id: string) {
      return request<{ article: Article }>(`/lager/articles/${id}`);
    },
    /**
     * Artikel unterwegs anlegen (z. B. beim Zählen in der Inventur). Sofort
     * nutzbar, geht als „ungeprüft" in die Admin-Kontrolle.
     */
    async lagerQuickCreateArticle(body: {
      name: string;
      articleNumber?: string;
      manufacturer?: string;
      manufacturerPartNumber?: string;
      barcode?: string;
      reviewNote?: string;
    }) {
      return request<{ article: Article }>(`/lager/articles/quick-create`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    /** Offene Artikel-Prüfungen (Admin). */
    async lagerListArticleReviews(status: 'PENDING' | 'ALL' = 'PENDING') {
      return request<{ items: ArticleReviewRow[] }>(
        `/lager/articles/review${buildQuery({ status })}`
      );
    },
    async lagerApproveArticle(
      id: string,
      body: { name?: string; articleNumber?: string; category?: string; manufacturer?: string }
    ) {
      return request<{ article: Article }>(`/lager/articles/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerMergeArticle(id: string, targetArticleId: string) {
      return request<{
        result: {
          movedStockItems: number;
          mergedStockItems: number;
          movedMovements: number;
          movedInventoryLines: number;
          mergedInventoryLines: number;
          movedOtherItems: number;
        };
      }>(`/lager/articles/${id}/merge`, {
        method: 'POST',
        body: JSON.stringify({ targetArticleId }),
      });
    },
    async lagerCreateArticle(body: CreateArticleInput) {
      return request<{ article: Article }>(`/lager/articles`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerUpdateArticle(id: string, body: Partial<CreateArticleInput> & { isActive?: boolean }) {
      return request<{ article: Article }>(`/lager/articles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    async lagerGetArticlePurchaseHistory(articleId: string) {
      return request<ArticlePurchaseHistoryResponse>(`/lager/articles/${articleId}/purchase-history`);
    },

    // --- Movements ---
    async lagerListMovements(
      opts?: ListOpts & {
        articleId?: string;
        warehouseId?: string;
        binId?: string;
        type?: string;
        source?: string;
        from?: string;
        to?: string;
      }
    ) {
      return request<MovementsResponse>(`/lager/movements${buildQuery(opts)}`);
    },
    async lagerCreateMovement(body: {
      articleId: string;
      warehouseId: string;
      binId: string;
      quantityDelta: number;
      reason: string;
      serials?: string[];
    }) {
      return request<unknown>(`/lager/movements`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** Warenausgang: bucht Bestand aus (negative ISSUE-Bewegung). */
    async lagerIssueStock(body: {
      articleId: string;
      warehouseId: string;
      binId: string;
      quantity: number;
      reason: string;
      serials?: string[];
    }) {
      return request<unknown>(`/lager/issues`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    // --- Bestandssuche über alle Lager ---
    async lagerStockSearch(opts?: ListOpts & { articleId?: string }) {
      return request<StockSearchResponse>(`/lager/stock-search${buildQuery(opts)}`);
    },

    /** Umlagerung: verschiebt Bestand atomar von einem Bin in einen anderen. */
    async lagerTransferStock(body: {
      articleId: string;
      fromWarehouseId: string;
      fromBinId: string;
      toWarehouseId: string;
      toBinId: string;
      quantity: number;
      reason: string;
      serials?: string[];
    }) {
      return request<{ referenceId: string }>(`/lager/transfers`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    // --- Mindestbestand ---
    async lagerListLowStock(opts?: { warehouseId?: string; page?: number; pageSize?: number }) {
      return request<LowStockResponse>(`/lager/low-stock${buildQuery(opts)}`);
    },
    async lagerGetLowStockCount(warehouseId?: string) {
      return request<{ count: number }>(`/lager/low-stock/count${buildQuery(warehouseId ? { warehouseId } : undefined)}`);
    },

    // --- Bestandswert (KIAN) ---
    async lagerGetValuation() {
      return request<StockValuation>(`/lager/valuation`);
    },
    async lagerRefreshValuation(all = false) {
      return request<ValuationRefreshResult>(`/lager/valuation/refresh${all ? '?all=true' : ''}`, {
        method: 'POST',
      });
    },

    // --- Rückgaben ---
    async lagerCreateReturn(body: CreateReturnInput) {
      return request<{ return: StockReturn }>(`/lager/returns`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerListReturns(opts?: {
      status?: string;
      technicianId?: string;
      page?: number;
      pageSize?: number;
    }) {
      return request<StockReturnsResponse>(`/lager/returns${buildQuery(opts)}`);
    },
    async lagerGetReturn(id: string) {
      return request<{ return: StockReturn }>(`/lager/returns/${id}`);
    },
    async lagerReviewReturn(id: string, body: ReviewReturnInput) {
      return request<{ return: StockReturn }>(`/lager/returns/${id}/review`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    // --- Auslieferungsaufträge ---
    async lagerCreateDeliveryOrder(body: CreateDeliveryOrderInput) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerListDeliveryOrders(opts?: {
      status?: DeliveryOrderStatus;
      customerId?: string;
      assignedTechnicianId?: string;
      page?: number;
      pageSize?: number;
    }) {
      return request<DeliveryOrdersResponse>(`/lager/delivery-orders${buildQuery(opts)}`);
    },
    async lagerGetDeliveryOrder(id: string) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders/${id}`);
    },
    async lagerPickDeliveryOrder(id: string, body: PickDeliveryOrderInput) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders/${id}/pick`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerIssueDeliveryOrder(id: string, body: IssueDeliveryOrderInput) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders/${id}/issue`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerCancelDeliveryOrder(id: string, note?: string | null) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ note: note ?? null }),
      });
    },
    async lagerMarkDeliveryOrderBilled(id: string) {
      return request<{ order: DeliveryOrder }>(`/lager/delivery-orders/${id}/mark-billed`, {
        method: 'POST',
      });
    },
    async lagerDownloadDeliveryOrderPdf(id: string): Promise<void> {
      const res = await fetch(`/api/lager/delivery-orders/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) {
        let msg = 'Liefernachweis konnte nicht geladen werden';
        try { const j = await res.json() as { error?: string }; msg = j.error ?? msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },

    // --- Kunden-Material-Historie ---
    async lagerGetCustomerMaterialHistory(
      customerId: string,
      opts?: {
        from?: string;
        to?: string;
        technicianId?: string;
        articleQuery?: string;
        withSerialOnly?: boolean;
        page?: number;
        pageSize?: number;
      }
    ) {
      return request<MaterialHistoryResponse>(
        `/lager/customers/${encodeURIComponent(customerId)}/material-history${buildQuery(opts)}`
      );
    },

    async lagerRetryReturnPdf(id: string): Promise<{ cwPdfUploaded: boolean }> {
      return request<{ cwPdfUploaded: boolean }>(`/lager/returns/${id}/retry-pdf`, { method: 'POST' });
    },

    async lagerDownloadReturnPdf(id: string): Promise<void> {
      const res = await fetch(`/api/lager/returns/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) {
        let msg = 'PDF konnte nicht geladen werden';
        try { const j = await res.json() as { error?: string }; msg = j.error ?? msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },

    // --- Reservierungen ---
    async lagerReserveStock(body: ReservationInput) {
      return request<ReservationResult>(`/lager/reservations`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    async lagerReleaseReservation(body: ReleaseReservationInput) {
      return request<ReservationResult>(`/lager/reservations/release`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    // --- SNR-Lebenslauf ---
    async lagerSearchSerials(q: string) {
      return request<{ serials: string[] }>(`/lager/serials${buildQuery({ q })}`);
    },
    async lagerGetSerialHistory(serial: string) {
      return request<SerialHistoryResult>(`/lager/serials/${encodeURIComponent(serial)}/history`);
    },
  };
}
